<template>
  <div>
    <app-spinner v-if="!data_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__lead><div class="table-default__left">Lead</div></template>
        <template #links="{ row }">
          <div class="table-default__controls">
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__activity table-default__activity--itinerary"
              :class="{ 'table-default__activity--active': row.story_id }"
            />
            <a
              :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
              class="table-default__activity table-default__activity--clients"
              :class="{ 'table-default__activity--active': row.hash }"
            />
          </div>
        </template>
        <template #invoice="{ row }">
          <div class="table-default__controls">
            <a class="table-default__link-border" @click="onShowRequestModal(row)">{{ row.number }}</a>
          </div>
        </template>
        <template #lead="{ row }">
          <div v-if="row.itinerary?.lead" class="table-default__left table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span>{{ row.itinerary.lead.name || '-' }}</span>
          </div>
          <span v-else>-</span>
        </template>
        <template #payable="{ row }">
          {{ row.amount_payable_usd ? `${formatString(row.amount_payable_usd)} $` : '-'}}
        </template>
        <template #commission="{ row }">
          {{ row.amount_payable_with_commission ? `${formatString(row.amount_payable_with_commission)} $` : '-'}}
        </template>
        <template #completed="{ row }">
          <span v-if="!row.auth_form_data?.completion_date" class="status-tag" :style="{ 'backgroundColor': '#8a8a8a' }">No</span>
          <span v-else>{{ dateTimeFormat(row.auth_form_data.completion_date) }}</span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
      <template v-if="table_close.items.length">
        <app-cells><h2 class="title">Close invoice</h2></app-cells>
        <v-client-table
          :data="table_close.items"
          :columns="table_close.columns"
          :options="table_close.options"
          class="table-default"
          :class="{ 'table-default--loading': table_close_preloader }"
        >
          <template #h__lead><div class="table-default__left">Lead</div></template>
          <template #links="{ row }">
            <div class="table-default__controls">
              <a
                :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': row.story_id }"
              />
              <a
                :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
                target="_blank"
                class="table-default__activity table-default__activity--clients"
                :class="{ 'table-default__activity--active': row.hash }"
              />
            </div>
          </template>
          <template #invoice="{ row }">{{ row.number }}</template>
          <template #lead="{ row }">
            <div v-if="row.itinerary?.lead" class="table-default__left table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
              <span>{{ row.itinerary.lead.name || '-' }}</span>
            </div>
            <span v-else>-</span>
          </template>
          <template #bank="{ row }">
            <template v-if="row.auth_data_charge_info.pos_transaction">
              {{ row.auth_data_charge_info.pos_transaction.bank.short_name }} ({{ row.auth_data_charge_info.pos_transaction.channel.name }})
            </template>
            <template v-else>-</template>
          </template>
          <template #amount="{ row }">
            <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.amount) }} $</template>
            <template v-else>-</template>
          </template>
          <template #commission="{ row }">
            <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.commission) }} $</template>
            <template v-else>-</template>
          </template>
          <template #settlement="{ row }">
            <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.settlement_amount) }} $</template>
            <template v-else>-</template>
          </template>
          <template #rrn="{ row }">
            <template v-if="row.auth_data_charge_info.pos_transaction">{{ row.auth_data_charge_info.pos_transaction.rrn }}</template>
            <template v-else>-</template>
          </template>
          <template #slip="{ row }">
            <a
              v-if="row.auth_data_charge_info.pos_transaction"
              :href="row.auth_data_charge_info.pos_transaction.machine_slip.file"
              target="_blank"
              class="status-tag"
              style="background-color: #439ad1"
            >
              View
            </a>
            <template v-else>-</template>
          </template>
          <template #charged_by="{ row }">
            <div v-if="row.auth_data_charge_info" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.auth_data_charge_info.charged_by.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: row.auth_data_charge_info.charged_by.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
              <span>{{ dateTimeFormat(row.auth_data_charge_info.charged_at) }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationClose"
              v-if="pagination_close.pages > 1"
              v-model="pagination_close.page"
              :page-count="pagination_close.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_close.count" :count="table_close.items.length" :page="pagination_close.page" />
        </app-cells>
      </template>
    </template>
    <modal
      name="request-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request authorization form charge</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Amount ($)" label-for="amount" required>
                  <app-fin-number
                    v-model="pos_transaction.amount"
                    :error="$v.pos_transaction.amount.$error"
                    id="amount"
                    name="amount"
                    placeholder="0"
                  />
                  <template #error>
                    <div v-if="$v.pos_transaction.amount.$dirty && !$v.pos_transaction.amount.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Bank" required>
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of bank_options"
                      :key="radio.id"
                      v-model="pos_transaction.bank"
                      :value="radio.id"
                      name="filterBank"
                      :checked="form.bank === radio.id"
                    >
                      <template #label>{{ radio.short_name }}</template>
                    </app-radio>
                  </app-cells>
                </app-form-group>
                <app-form-group label="Channel" required>
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of channel_options"
                      :key="radio.code"
                      v-model="pos_transaction.channel"
                      :value="radio.code"
                      name="filterCreditCard"
                      :checked="form.channel === radio.code"
                    >
                      <template #label>{{ radio.name }}</template>
                    </app-radio>
                  </app-cells>
                </app-form-group>
                <app-form-group label="RRN" required label-for="rrn">
                  <app-input
                    v-model="pos_transaction.rrn"
                    placeholder="RRN"
                    id="rrn"
                    name="rrn"
                    type="number"
                    :error="$v.pos_transaction.rrn.$error"
                  />
                  <template #error>
                    <div v-if="$v.pos_transaction.rrn.$dirty && !$v.pos_transaction.rrn.required">Required field</div>
                    <div v-if="$v.pos_transaction.rrn.$dirty && !$v.pos_transaction.rrn.maxLength">Length of RRN must be equal to 12</div>
                    <div v-if="$v.pos_transaction.rrn.$dirty && !$v.pos_transaction.rrn.minLength">Length of RRN must be equal to 12</div>
                  </template>
                </app-form-group>
                <app-form-group label="Machine slip" required>
                  <app-uploader
                    v-model="pos_transaction.machine_slip"
                    class="custom-uploader--flex"
                    :extensions="['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                  />
                  <template #error>
                    <div v-if="$v.pos_transaction.machine_slip.$dirty && !$v.pos_transaction.machine_slip.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Card number">
                  <app-input
                    v-model="form.card_number"
                    readonly
                  />
                </app-form-group>
                <app-form-group label="Card type">
                  <app-input
                    v-model="form.card_type"
                    readonly
                  />
                </app-form-group>
                <app-cells :indent="false" :wrap="false">
                  <app-form-group label="Valid until">
                    <app-input
                      v-model="form.valid_until"
                      readonly
                    />
                  </app-form-group>
                  <app-form-group label="CVV">
                    <app-input
                      v-model="form.cvv"
                      readonly
                    />
                  </app-form-group>
                </app-cells>
              </template>
            </app-grid>
            <app-cells position="between" :indent="false">
              <app-button ref="print" theme="warn" @click.native="onPrint(invoice_to_update)">Print</app-button>
              <app-button ref="submit" type="submit" :disabled="$v.pos_transaction.$error">Charged</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getFinanceInvoiceList,
  patchFinanceInvoiceAuthData,
  getFinanceAuthDataExport,
  getFinanceBanks,
  getCardTypes
} from '@/http'
import { format, parse, parseISO } from 'date-fns'
import { required, maxLength, minLength } from 'vuelidate/lib/validators'

export default {
  name: 'AuthorizationForms',
  data() {
    return {
      rrn: null,
      machine_slip: null,
      slip_preview: null,
      data_loaded: false,
      table: {
        items: [],
        columns: [
          'links',
          'invoice',
          'lead',
          'payable',
          'commission',
          'completed',
        ],
        options: {
          headings: {
            'links': 'Links',
            'invoice': 'Invoice',
            'lead': 'Lead',
            'payable': 'Payable',
            'commission': 'With commission',
            'completed': 'Completed',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      table_close: {
        items: [],
        columns: [
          'links',
          'invoice',
          'lead',
          'bank',
          'amount',
          'commission',
          'settlement',
          'rrn',
          'slip',
          'charged_by',
        ],
        options: {
          headings: {
            'links': 'Links',
            'invoice': 'Invoice',
            'lead': 'Lead',
            'bank': 'Bank (Channel)',
            'amount': 'Charged amount',
            'rrn': 'RRN',
            'commission': 'Commission',
            'settlement': 'Settlement',
            'slip': 'Slip',
            'charged_by': 'Charged by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_close: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_close_preloader: false,
      bank_options: [],
      channel_options: [],
      form: {
        signature: '',
      },
      pos_transaction: {},
      invoice_to_update: null,
    }
  },
  validations: {
    pos_transaction: {
      amount: { required },
      rrn: {
        required,
        maxLength: maxLength(12),
        minLength: minLength(12),
      },
      machine_slip: { required },
      bank: { required },
      channel: { required },
    }
  },
  created() {
    getFinanceBanks({pos: true}).then(response => { this.bank_options = response.data })
    getCardTypes().then(response => { this.channel_options = response.data })
    this.fetchItems()
    this.fetchItemsClose()
  },
  methods: {
    fetchItems() {
      getFinanceInvoiceList({ page: this.pagination.page, page_size: 40, authorization_form_completed: true, authorization_form_charged: false })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.data_loaded = true
          this.table_preloader = false
        })
    },
    fetchItemsClose() {
      getFinanceInvoiceList({ page: this.pagination.page, page_size: 40, authorization_form_completed: true, authorization_form_charged: true, status: 'sent' })
        .then(response => {
          this.table_close.items = response.data.results
          this.pagination_close.pages = response.data.pages
          this.pagination_close.count = response.data.count
          this.table_close_preloader = false
        })
    },
    onShowRequestModal(data) {
      this.invoice_to_update = data.id
      this.form = {
        card_number: data.auth_form_data.card_number,
        card_type: data.auth_form_data.card_type.name,
        cvv: data.auth_form_data.cvv,
        valid_until: data.auth_form_data.valid_until ? format(parse(data.auth_form_data.valid_until, 'yyyy-MM-dd', new Date()), 'MM / yyyy') : '',
      }
      this.pos_transaction = {
        amount: data.amount_payable_with_commission
      }
      this.$v.pos_transaction.$reset()
      this.$modal.show('request-modal')
    },
    onCheckForm() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchFinanceInvoiceAuthData(this.invoice_to_update, {
        pos_transaction: {...this.pos_transaction, machine_slip: this.pos_transaction.machine_slip.id},
        charged: true
      })
        .then(() => {
          this.fetchItems()
          this.fetchItemsClose()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('request-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationClose(page) {
      this.table_close_preloader = true
      this.pagination_close.page = page
      this.fetchItemsClose()
    },
    onPrint(id) {
      this.$refs.print.preload = true
      getFinanceAuthDataExport(id)
        .finally(() => { this.$refs.print.preload = false })
        .then(response => { window.open(response.data) })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('ru-RU').format(string) : '-'
    },
  }
}
</script>

<style lang="sass" scoped>
.form-signature
  display: block
  max-height: 120px
</style>
